import { useFormikContext } from 'formik';
import { FC, useCallback, useEffect, useMemo } from 'react';

import { FetchError } from '@api/common/types';

import { useError } from '@context/modal';

import { FormValues } from '../../../../types';
import { getFieldName, isBrandFilter, isNumberArray, normalizeFieldName } from '../../../../utils';
import { useFiltersData } from '../../hooks/useFiltersData';
import { useFiltersProcessing } from '../../hooks/useFiltersProcessing';
import { usePriceRange } from '../../hooks/usePriceRange';
import DesktopFiltersPopupContent from '../DesktopFiltersPopupContent';
import MobileFiltersPopupContent from '../MobileFiltersPopupContent';
import { PopupFormContentInnerProps } from './types';

const PopupFormContentInner: FC<PopupFormContentInnerProps> = ({
    filterData,
    hidePriceFilter,
    isBrandPage = false,
    priceRange,
    fieldPrefix,
    rangeDefault,
    formKey,
    isOpen,
    baseFiltersFromProps,
    onClose,
    onApplyClick,
    rememberedPrice,
    setRememberedPrice,
    lastChangedBlockRef,
}) => {
    const { values, setFieldValue, initialValues } = useFormikContext<FormValues>();

    const isMobile = fieldPrefix === 'mobile';

    const baseFilters =
        baseFiltersFromProps && Object.keys(baseFiltersFromProps).length > 0 ? baseFiltersFromProps : null;

    const priceFieldName = getFieldName('price', fieldPrefix);
    const priceValue = initialValues[priceFieldName];
    const savedPrice = priceValue !== undefined && isNumberArray(priceValue) ? priceValue : undefined;
    const priceFromUrl = savedPrice;

    const originalBrands = useMemo(() => {
        return filterData.filter(isBrandFilter);
    }, [filterData]);

    const lastChangedBlockCode =
        lastChangedBlockRef.current.length > 0
            ? lastChangedBlockRef.current[lastChangedBlockRef.current.length - 1]
            : undefined;
    const lastChangedBlockHistory = lastChangedBlockRef.current;

    const { preparedFormFilters, preparedFiltersWithoutLast, shouldMakeRequest } = useFiltersProcessing({
        values,
        fieldPrefix,
        priceRange,
        baseFilters,
        isMobile,
        rememberedPrice,
        isOpen,
        currentFiltersData: null,
        previousFiltersData: null,
        baseFiltersData: null,
        lastChangedBlockCode,
        lastChangedBlockHistory,
        originalBrands,
    });

    const {
        currentFiltersData,
        previousFiltersData,
        baseFiltersData,
        isLoading,
        hasError,
        currentError,
        previousError,
        baseError,
    } = useFiltersData({
        preparedFormFilters,
        preparedFiltersWithoutLast,
        baseFilters,
        isOpen,
        shouldMakeRequest,
    });

    const { preparedFiltersFromRequest } = useFiltersProcessing({
        values,
        fieldPrefix,
        priceRange,
        baseFilters,
        isMobile,
        rememberedPrice,
        isOpen,
        currentFiltersData,
        previousFiltersData,
        baseFiltersData,
        lastChangedBlockCode,
        lastChangedBlockHistory,
        originalBrands,
    });

    const errorToShow = useMemo(() => {
        if (hasError) {
            const error = currentError || previousError || baseError;

            if (error) {
                return new FetchError('Что-то пошло не так', error.status, '');
            }
        }

        return null;
    }, [hasError, currentError, previousError, baseError]);

    useError(errorToShow);

    const hasFormValuesChanged = useMemo(() => {
        const priceFieldName = getFieldName('price', fieldPrefix);
        const isPreOrderFieldName = getFieldName('popup_isPreOrder', fieldPrefix);

        const hasFilterChanges = Object.keys(values).some(key => {
            if (key === priceFieldName || key === isPreOrderFieldName) return false;
            const currentValue = values[key];
            const initialValue = initialValues[key];

            if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
                if (currentValue.length !== initialValue.length) return true;

                return !currentValue.every(currentItem => {
                    return initialValue.some(initialItem => initialItem === currentItem);
                });
            }

            return currentValue !== initialValue;
        });

        const currentPriceValue = values[priceFieldName];
        const initialPriceValue = initialValues[priceFieldName];
        const currentPrice =
            currentPriceValue !== undefined && isNumberArray(currentPriceValue) ? currentPriceValue : undefined;
        const initialPrice =
            initialPriceValue !== undefined && isNumberArray(initialPriceValue) ? initialPriceValue : undefined;
        const hasPriceChanged =
            currentPrice && initialPrice
                ? currentPrice[0] !== initialPrice[0] || currentPrice[1] !== initialPrice[1]
                : currentPrice !== initialPrice;

        const currentIsPreOrder = values[isPreOrderFieldName];
        const initialIsPreOrder = initialValues[isPreOrderFieldName];
        const hasIsPreOrderChanged = currentIsPreOrder !== initialIsPreOrder;

        return hasFilterChanges || hasPriceChanged || hasIsPreOrderChanged;
    }, [values, initialValues, fieldPrefix]);

    const currentFilterData = useMemo(() => {
        if (!isOpen) {
            return filterData;
        }

        return hasFormValuesChanged && preparedFiltersFromRequest ? preparedFiltersFromRequest : filterData;
    }, [isOpen, filterData, preparedFiltersFromRequest, hasFormValuesChanged, formKey]);

    const { currentPriceRange, currentRangeDefault } = usePriceRange({
        currentFiltersData,
        isOpen,
        priceRange,
        rangeDefault,
        formKey,
        rememberedPrice,
        values,
        fieldPrefix,
        setFieldValue,
    });

    const currentTotalCount = useMemo(() => {
        const productsCount = currentFiltersData?.products_count ?? baseFiltersData?.products_count;

        return productsCount !== undefined && productsCount !== null ? productsCount : undefined;
    }, [currentFiltersData?.products_count, baseFiltersData?.products_count]);

    const handleFilterChange = useCallback(
        (fieldName: string, _newValue?: (string | number)[]) => {
            const normalizedKey = normalizeFieldName(fieldName, fieldPrefix);

            const newValue =
                _newValue !== undefined ? _newValue : (values[fieldName] as (string | number)[] | undefined);

            if (newValue === undefined) {
                return;
            }

            if (Array.isArray(newValue) && newValue.length === 0) {
                lastChangedBlockRef.current = lastChangedBlockRef.current.filter(key => key !== normalizedKey);

                return;
            }

            const existingIndex = lastChangedBlockRef.current.indexOf(normalizedKey);

            if (existingIndex >= 0) {
                lastChangedBlockRef.current = [
                    ...lastChangedBlockRef.current.filter(key => key !== normalizedKey),
                    normalizedKey,
                ];
            } else {
                lastChangedBlockRef.current = [...lastChangedBlockRef.current, normalizedKey];
            }
        },
        [fieldPrefix, values]
    );

    useEffect(() => {
        if (hasError) {
            onClose();
        }
    }, [hasError, onClose]);

    if (!isMobile) {
        return (
            <DesktopFiltersPopupContent
                isLoading={isLoading}
                isBrandPage={isBrandPage}
                filterData={currentFilterData}
                fieldPrefix={fieldPrefix}
                currentPriceRange={currentPriceRange}
                currentRangeDefault={currentRangeDefault}
                setRememberedPrice={setRememberedPrice}
                onClose={onClose}
                currentTotalCount={currentTotalCount}
                hidePriceFilter={hidePriceFilter}
                rememberedPrice={rememberedPrice}
                priceFromUrl={priceFromUrl}
                priceRange={priceRange}
                rangeDefault={rangeDefault}
                currentFilterData={currentFilterData}
                formKey={formKey}
                onApplyClick={onApplyClick}
                onFilterChange={handleFilterChange}
            />
        );
    }

    return (
        <MobileFiltersPopupContent
            isLoading={isLoading}
            isBrandPage={isBrandPage}
            filterData={currentFilterData}
            fieldPrefix={fieldPrefix}
            currentPriceRange={currentPriceRange}
            currentTotalCount={currentTotalCount}
            hidePriceFilter={hidePriceFilter}
            priceRange={priceRange}
            rangeDefault={rangeDefault}
            currentFilterData={currentFilterData}
            onApplyClick={onApplyClick}
            onFilterChange={handleFilterChange}
        />
    );
};

export default PopupFormContentInner;
